<script setup lang="ts">
import type { Form, FormErrorEvent, FormSubmitEvent, StepperItem } from '#ui/types';
import { adaptFormDataToDto, getAddressFormFieldSchema, phoneValidator } from '@lib/shared';
import { useMutation } from '@tanstack/vue-query';
import lodashFlatten from 'lodash-es/flatten';
import { z } from 'zod';

import { apiPostSellIntent, type BackendErrorEntity, AssetEuroNorm, type BodyType, type ErrorType, type PostSellIntentBodyDto } from '~/apiClient';
import { AUTOCOMPLETE_OFF } from '~/constants/form';
import { PlatformBusClass } from '~/types/asset.type';

export interface StoryblokSellFormProps {
    blok: StoryblokSellForm;
}

// PROPS
const { blok } = defineProps<StoryblokSellFormProps>();

// COMPOSABLES
const { logError, logInfo } = useLogs();
const { tt, locale } = useTypedI18n();
const { openAuthModal } = useModals();
const { notifyBackendError, notifyError, notifyGenericError } = useNotification();
const { getFormErrors } = useBackendErrors();
const { data: session, isLoggedIn } = useAuthUtils();
const { getToken, formatRequestOptions } = useInteractionOnlyTurnstile('sell');
const { mutateAsync, isPending } = useMutation<
    Awaited<ReturnType<typeof apiPostSellIntent>>,
    ErrorType<BackendErrorEntity>,
    { data: BodyType<PostSellIntentBodyDto>; token: string | null },
    unknown
>({
    mutationFn: ({ data, token }) => apiPostSellIntent(data, formatRequestOptions(token)),
});

// DATA
const isSuccess = ref(false);
const isSubmitting = ref(false);
const isFormBusy = computed(() => isSubmitting.value || isPending.value);
const stepperItems: StepperItem[] = [
    {
        title: tt('storyblok.sellForm.busStep'),
        icon: 'i-lucide-bus-front',
        value: 'bus',
    },
    {
        title: tt('storyblok.sellForm.contactsStep'),
        icon: 'i-lucide-user',
        value: 'contacts',
    },
];
const activeStep = ref<'bus' | 'contacts'>('bus');

const classesItems = Object.values(PlatformBusClass).map(value => ({ value, label: tt(`asset.class.${value}`) }));
const euroNormItems = Object.values(AssetEuroNorm).map(value => ({ value, label: tt(`asset.euroNorm.${value}`) }));
const busFormSchema = z.object({
    vehiclesNumber: z.number({ error: tt('validation.msg.required', { field: tt('storyblok.sellForm.vehiclesNumber.field') }) }),
    vehiclesClasses: z
        .array(z.enum(PlatformBusClass), {
            error: tt('validation.msg.invalid', { field: tt('storyblok.sellForm.vehiclesClasses.field') }),
        })
        .min(1, { error: tt('validation.msg.invalid', { field: tt('storyblok.sellForm.vehiclesClasses.field') }) }),
    vehiclesEuroNorms: z
        .array(z.enum(AssetEuroNorm), {
            error: tt('validation.msg.invalid', { field: tt('storyblok.sellForm.vehiclesEuroNorms.field') }),
        })
        .min(1, { error: tt('validation.msg.invalid', { field: tt('storyblok.sellForm.vehiclesEuroNorms.field') }) }),
    vehiclesAddress: getAddressFormFieldSchema(tt('validation.msg.required', { field: tt('storyblok.sellForm.vehiclesAddress.field') })).refine(
        vehiclesAddress => !!vehiclesAddress,
        tt('validation.msg.required', { field: tt('storyblok.sellForm.vehiclesAddress.field') })
    ),
    notes: z.string().optional(),
});
type BusFormSchema = z.infer<typeof busFormSchema>;
const busFormData = reactive<Partial<BusFormSchema>>({
    vehiclesNumber: undefined,
    vehiclesClasses: [],
    vehiclesEuroNorms: [],
    vehiclesAddress: undefined,
    notes: undefined,
});
const busForm = useTemplateRef<Form<BusFormSchema>>('busForm');
const addressErrorMessage = ref<string | undefined>();

const contactsFormSchema = z.object({
    // The whole option, not just the ISO code: it is what BaseCountrySelect models and what the phone input reads.
    country: z.object(
        {
            countryCode: z.string(),
            taxIdMandatory: z.boolean().optional(),
        },
        { error: tt('validation.msg.required', { field: tt('storyblok.sellForm.country') }) }
    ),
    tin: zodRequiredString(tt('validation.msg.required', { field: tt('storyblok.sellForm.company.base') })),
    email: z.email(tt('validation.msg.email')),
    firstname: z.string().optional(),
    lastname: z.string().optional(),
    phone: z.string().refine(value => !value || phoneValidator(value), {
        message: tt('validation.msg.phone'),
    }),
});
type ContactsFormSchema = z.infer<typeof contactsFormSchema>;
const contactsFormData = reactive<Partial<ContactsFormSchema>>({
    country: undefined,
    tin: undefined,
    email: undefined,
    firstname: undefined,
    lastname: undefined,
    phone: '',
});
const contactsForm = useTemplateRef<Form<ContactsFormSchema>>('contactsForm');

// METHODS
// Wrapped so a null token (turnstile disabled) stays distinguishable from a failed challenge.
const resolveTurnstileToken = async (): Promise<{ token: string | null } | null> => {
    try {
        return { token: await getToken() };
    } catch {
        notifyError({ title: tt('validation.msg.captcha'), description: tt('notifications.tryAgain') });

        return null;
    }
};

const handleBusFormBackendError = (apiError: ErrorType<BackendErrorEntity>): void => {
    const formErrors = getFormErrors<PostSellIntentBodyDto, BusFormSchema>(apiError, {
        vehiclesNumber: 'vehiclesNumber',
        vehiclesClasses: 'vehiclesClasses',
        vehiclesEuroNorms: 'vehiclesEuroNorms',
        vehiclesAddress: 'vehiclesAddress',
    });
    busForm.value?.setErrors(formErrors);

    scrollToFormError();
};

const onBusSubmit = async (event: FormSubmitEvent<BusFormSchema>): Promise<void> => {
    const { data } = event;

    if (!isLoggedIn.value || !session.value?.user) {
        activeStep.value = 'contacts';

        return;
    }

    isSubmitting.value = true;

    try {
        const turnstile = await resolveTurnstileToken();

        if (!turnstile) return;

        const payload: PostSellIntentBodyDto = adaptFormDataToDto({
            ...data,
            vehiclesClasses: lodashFlatten(data.vehiclesClasses.map(getBusClassesFromPlatformBusClass)),
            vehiclesAddress: data?.vehiclesAddress,
        });

        await mutateAsync({ data: payload, token: turnstile.token })
            .then(() => (isSuccess.value = true))
            .catch((apiError: ErrorType<BackendErrorEntity>) => {
                logError('Error while posting sell intent step bus', { error: apiError });

                if (!apiError.data?.error) {
                    notifyGenericError();

                    return;
                }

                const { error } = apiError.data;
                notifyBackendError({ error });
                handleBusFormBackendError(apiError);
            });
    } finally {
        isSubmitting.value = false;
    }
};

const onBusError = (errorEvent: FormErrorEvent): void => {
    addressErrorMessage.value =
        busForm.value?.getErrors('vehiclesAddress') && tt('validation.msg.invalid', { field: tt('storyblok.sellForm.vehiclesAddress.field') });

    logInfo('Error before sell intent post step bus', {
        formErrors: errorEvent.errors,
        busFormData,
    });

    if (!errorEvent.errors.length) return;

    scrollToFormError();
};

const onContactsSubmit = async (event: FormSubmitEvent<ContactsFormSchema>): Promise<void> => {
    const { data: contactsData } = event;
    const busValidation = busFormSchema.safeParse(busFormData);

    if (!busValidation.success) {
        activeStep.value = 'bus';
        notifyGenericError();

        return;
    }

    isSubmitting.value = true;

    try {
        const turnstile = await resolveTurnstileToken();

        if (!turnstile) return;

        const payload: PostSellIntentBodyDto = adaptFormDataToDto({
            vehiclesNumber: busValidation.data.vehiclesNumber,
            vehiclesEuroNorms: busValidation.data.vehiclesEuroNorms,
            vehiclesClasses: lodashFlatten(busValidation.data.vehiclesClasses.map(getBusClassesFromPlatformBusClass)),
            vehiclesAddress: busValidation.data.vehiclesAddress,
            notes: busValidation.data.notes,
            email: contactsData.email,
            tin: contactsData.tin,
            countryCode: contactsData.country.countryCode,
            language: localeToLanguage(locale.value),
            firstname: contactsData.firstname,
            lastname: contactsData.lastname,
            phone: contactsData.phone,
        });

        await mutateAsync({ data: payload, token: turnstile.token })
            .then(() => (isSuccess.value = true))
            .catch((apiError: ErrorType<BackendErrorEntity>) => {
                logError('Error while posting sell intent step contacts', { error: apiError });

                if (!apiError.data?.error) {
                    notifyGenericError();

                    return;
                }

                const { error } = apiError.data;
                notifyBackendError({ error });

                if (
                    apiError.data?.error?.property &&
                    ['vehiclesNumber', 'vehiclesClasses', 'vehiclesEuroNorms', 'vehiclesAddress'].includes(apiError.data?.error?.property)
                ) {
                    activeStep.value = 'bus';

                    handleBusFormBackendError(apiError);

                    return;
                }

                const formErrors = getFormErrors<PostSellIntentBodyDto, ContactsFormSchema>(apiError, {
                    email: 'email',
                    firstname: 'firstname',
                    lastname: 'lastname',
                    phone: 'phone',
                    tin: 'tin',
                    countryCode: 'country',
                });
                contactsForm.value?.setErrors(formErrors);

                scrollToFormError();
            });
    } finally {
        isSubmitting.value = false;
    }
};

const onContactsError = (errorEvent: FormErrorEvent): void => {
    logInfo('Error before sell intent post step contacts', {
        formErrors: errorEvent.errors,
        busFormData,
    });

    if (!errorEvent.errors.length) return;

    scrollToFormError();
};
</script>

<template>
    <div v-editable="blok" class="flex flex-col gap-4 rounded-xl bg-white py-6">
        <Transition
            v-if="isSuccess"
            enter-from-class="opacity-0"
            leave-to-class="opacity-0"
            enter-active-class="transition-opacity duration-300"
            leave-active-class="transition-opacity duration-300"
        >
            <div v-if="isSuccess" class="my-auto flex flex-col gap-6 px-6">
                <div class="flex flex-col gap-2">
                    <h3 class="text-2xl font-bold">{{ tt('storyblok.sellForm.success.title') }}</h3>
                    <p>{{ tt('storyblok.sellForm.success.description') }}</p>
                </div>
                <template v-if="!isLoggedIn">
                    <USeparator />
                    <div class="flex flex-col gap-2">
                        <h3 class="text-lg font-semibold">{{ tt('storyblok.sellForm.success.guest.title') }}</h3>
                        <p>{{ tt('storyblok.sellForm.success.guest.description') }}</p>
                        <UButton class="w-auto" :label="tt('storyblok.sellForm.success.guest.cta')" size="xl" block @click="openAuthModal()" />
                    </div>
                </template>
            </div>
        </Transition>
        <template v-else>
            <BaseStepper v-if="!isLoggedIn" v-model="activeStep" :items="stepperItems" />
            <div class="px-6">
                <Transition
                    enter-from-class="opacity-0"
                    leave-to-class="opacity-0"
                    enter-active-class="transition-opacity duration-300"
                    leave-active-class="transition-opacity duration-[0]"
                >
                    <UForm
                        v-show="activeStep === 'bus'"
                        ref="busForm"
                        class="flex flex-col gap-4"
                        :state="busFormData"
                        :schema="busFormSchema"
                        @submit="onBusSubmit"
                        @error="onBusError"
                    >
                        <UFormField class="w-full" :label="tt('storyblok.sellForm.vehiclesNumber.base')" name="vehiclesNumber" required>
                            <UInput v-model.number="busFormData.vehiclesNumber" :disabled="isFormBusy" :autocomplete="AUTOCOMPLETE_OFF" />
                        </UFormField>
                        <UFormField class="w-full" :label="tt('storyblok.sellForm.vehiclesClasses.base')" name="vehiclesClasses" required>
                            <USelectMenu
                                v-model="busFormData.vehiclesClasses"
                                value-key="value"
                                :disabled="isFormBusy"
                                :items="classesItems"
                                :autocomplete="AUTOCOMPLETE_OFF"
                                multiple
                            />
                        </UFormField>
                        <UFormField class="w-full" :label="tt('storyblok.sellForm.vehiclesEuroNorms.base')" name="vehiclesEuroNorms" required>
                            <USelectMenu
                                v-model="busFormData.vehiclesEuroNorms"
                                value-key="value"
                                :disabled="isFormBusy"
                                :items="euroNormItems"
                                :autocomplete="AUTOCOMPLETE_OFF"
                                multiple
                            />
                        </UFormField>
                        <UFormField
                            :label="tt('storyblok.sellForm.vehiclesAddress.base')"
                            name="vehiclesAddress"
                            :error="addressErrorMessage"
                            required
                        >
                            <BaseAddressSearch
                                v-model="busFormData.vehiclesAddress"
                                :disabled="isFormBusy"
                                @update:model-value="addressErrorMessage = undefined"
                            />
                            <template #error="{ error }">
                                <span v-if="error" class="text-red inline-flex items-center gap-3 text-base leading-6">
                                    <UIcon name="lucide:triangle-alert" class="size-6" /> {{ error }}
                                </span>
                            </template>
                        </UFormField>
                        <UFormField class="w-full" :label="tt('storyblok.sellForm.notes')" name="notes">
                            <UTextarea v-model.trim="busFormData.notes" :disabled="isFormBusy" :autocomplete="AUTOCOMPLETE_OFF" />
                        </UFormField>
                        <UButton
                            :label="tt(`storyblok.sellForm.busCta.${isLoggedIn ? 'user' : 'guest'}`)"
                            class="mt-4"
                            type="submit"
                            size="xl"
                            :loading="isFormBusy"
                            block
                        />
                    </UForm>
                </Transition>
                <Transition
                    enter-from-class="opacity-0"
                    leave-to-class="opacity-0"
                    enter-active-class="transition-opacity duration-300"
                    leave-active-class="transition-opacity  duration-[0]"
                >
                    <UForm
                        v-show="activeStep === 'contacts'"
                        ref="contactsForm"
                        class="flex flex-col gap-4"
                        :state="contactsFormData"
                        :schema="contactsFormSchema"
                        @submit="onContactsSubmit"
                        @error="onContactsError"
                    >
                        <UFormField class="w-full" :label="tt('storyblok.sellForm.country')" name="country" required>
                            <BaseCountrySelect v-model="contactsFormData.country" :disabled="isFormBusy" />
                        </UFormField>
                        <UFormField class="w-full" :label="tt('storyblok.sellForm.company.base')" name="tin" required>
                            <UInput
                                v-model="contactsFormData.tin"
                                class="w-full"
                                :placeholder="tt('storyblok.sellForm.company.placeholder')"
                                :disabled="isFormBusy"
                                :autocomplete="AUTOCOMPLETE_OFF"
                            />
                        </UFormField>
                        <UFormField class="w-full" :label="tt('storyblok.sellForm.email.base')" required name="email">
                            <UInput v-model="contactsFormData.email" :disabled="isFormBusy" :autocomplete="AUTOCOMPLETE_OFF" />
                        </UFormField>
                        <div class="flex flex-col gap-4 lg:flex-row">
                            <UFormField class="w-full" :label="tt('storyblok.sellForm.firstname')" name="firstname">
                                <UInput v-model="contactsFormData.firstname" :disabled="isFormBusy" :autocomplete="AUTOCOMPLETE_OFF" />
                            </UFormField>
                            <UFormField class="w-full" :label="tt('storyblok.sellForm.lastname')" name="lastname">
                                <UInput v-model="contactsFormData.lastname" :disabled="isFormBusy" :autocomplete="AUTOCOMPLETE_OFF" />
                            </UFormField>
                        </div>
                        <UFormField class="w-full" :label="tt('storyblok.sellForm.phone')" name="phone">
                            <template #hint>
                                <BasePhoneHint />
                            </template>
                            <BasePhoneInput
                                v-model.trim="contactsFormData.phone"
                                :disabled="isFormBusy"
                                :country-code="contactsFormData.country?.countryCode"
                            />
                        </UFormField>
                        <UButton :label="tt('storyblok.sellForm.contactsCta')" class="mt-4" type="submit" size="xl" :loading="isFormBusy" block />
                    </UForm>
                </Transition>
                <TurnistileChallenge />
            </div>
        </template>
    </div>
</template>
